import { useEffect, useState } from "react";
import { Avatar, Badge, Group, Switch, Flex } from "@mantine/core";
import { useContext } from "react";
import { FEATURE_FLAGS } from "@congrs/schemas";

import { AuthContext } from "../../../services/context";
import { getCurrentWorkspace } from "../../../services/userConfig";
import {
  useGetTicketColumns,
  useUpdateColumnVisibility,
} from "../../../api/tickets";
import { useGetEvent } from "../../../api/event";
import { useGetAllFeatureFlags } from "../../../api/featureFlags";
import ColumnVisibility from "./ColumnVisibility";
import CreateCustomField from "../../Registrations/CreateCustomField";
import CustomFieldsList from "../../Registrations/CustomFieldList";

function TableSettings() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const { data: columns } = useGetTicketColumns({
    eventId: currentEventId,
  });

  const { data: event } = useGetEvent(currentEventId);
  const { data: flags } = useGetAllFeatureFlags({ eventId: currentEventId });
  const registrationsEnabled = Boolean(
    flags?.find((flag) => flag.featureName === FEATURE_FLAGS.REGISTRATIONS)
      ?.enabled
  );

  // Function to update all columns visibility
  const {
    mutate: updateColumnsVisibility,
    isSuccess: updateColumnsVisibilitySuccess,
  } = useUpdateColumnVisibility({
    eventId: currentEventId,
  });

  const [allColumns, setAllColumns] = useState([]); // All columns from the database
  const [visibleColumns, setVisibleColumns] = useState([]); // Visible columns
  const customColumns = columns?.filter((column) => column.isCustom) ?? [];

  useEffect(() => {
    if (columns) {
      const visible = columns.filter((column) => column.isVisible === true);

      // Map columns to add toggleable property and render function
      const allColumnsWithProps = addRenderAndToggleable(columns);
      const visibleColumnsWithProps = addRenderAndToggleable(visible);

      setAllColumns(allColumnsWithProps);
      setVisibleColumns(visibleColumnsWithProps);
    }
  }, [columns, updateColumnsVisibilitySuccess]);

  return (
    <Flex
      direction="column"
      grow={1}
      style={{ overflowY: "auto" }}
      rowGap="md"
      py="sm"
    >
      {event && flags && (
        <Flex direction="column" gap="md">
          <CreateCustomField
            event={event}
            appliedTo="ticket"
            registrationsEnabled={registrationsEnabled}
          />
          <CustomFieldsList
            event={event}
            appliedTo="ticket"
            registrationsEnabled={registrationsEnabled}
          />
        </Flex>
      )}
      <ColumnVisibility
        currentEventId={currentEventId}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        updateColumnsVisibility={updateColumnsVisibility}
        updateColumnsVisibilitySuccess={updateColumnsVisibilitySuccess}
        customColumns={customColumns}
        allColumns={allColumns}
      />
    </Flex>
  );
}

// Function to add custom render function and toggleable property to all columns
// to all columns except required fields for creating an ticket
const addRenderAndToggleable = (columns) => {
  return columns.map((column) => {
    switch (column.name) {
      case "picture":
        return {
          accessor: column.name,
          toggleable: true,
          render: (column) => (
            <Group justify="center">
              <Avatar
                src={column.picture}
                size="md"
                radius="sm"
                alt="avatar"
                color="initials"
                name={`${column.firstName} ${column.lastName}`}
              />
            </Group>
          ),
        };
      case "checkedInAt":
        return {
          accessor: column.name,
          title: "Checked in",
          toggleable: true,
          render: (column) => (
            <Group justify="center">
              <Switch checked={Boolean(column.checkedInAt)} disabled />
            </Group>
          ),
        };
      case "category":
        return {
          accessor: column.name,
          toggleable: true,
          render: (column) => (
            <Group justify="center">
              <Badge color={"gray"}>{column.category}</Badge>
            </Group>
          ),
        };
      default:
        return {
          accessor: column.name,
          toggleable: ["firstName", "lastName", "email"].includes(column.name)
            ? false
            : true,
        };
    }
  });
};

export default TableSettings;
